import React, { useEffect, useRef, useState } from "react";
import { api, useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MessageSquare, Send } from "lucide-react";

export default function OnboardingMessages({ onboardingId }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const load = () => api.get(`/onboarding/${onboardingId}/messages`).then(({ data }) => setMsgs(data || [])).catch(() => setMsgs(null));
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [onboardingId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ block: "nearest" }); }, [msgs]);
  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/onboarding/${onboardingId}/messages`, { body: body.trim() });
      setMsgs((m) => [...(m || []), data]); setBody("");
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };
  if (msgs === null) return null;
  const isAdmin = user?.role === "admin";
  return (
    <Card className="p-5 border-[#E9ECEF] rounded-sm mb-4 bg-white" id="messages" data-testid="onboarding-messages-panel">
      <div className="flex items-center gap-2 mb-1">
        <MessageSquare size={14} className="text-[#D4AF37]" />
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#D4AF37]">Messages</div>
      </div>
      <div className="text-xs text-[#6C757D] mb-3">{isAdmin ? "Messages go to the assigned support / production team." : "Messages go straight to the super-admins. They're notified in-portal and by email."}</div>
      <div className="max-h-72 overflow-y-auto space-y-2 pr-1" data-testid="onboarding-messages-list">
        {msgs.length === 0 && <div className="text-xs text-[#6C757D] italic">No messages yet.</div>}
        {msgs.map((m) => {
          const mine = m.author_id === user?.id;
          return (
            <div key={m.id} className={`max-w-[85%] ${mine ? "ml-auto" : ""}`} data-testid={`onboarding-message-${m.id}`}>
              <div className={`rounded-sm px-3 py-2 text-sm ${mine ? "bg-[#0B132B] text-white" : "bg-[#F8F9FA] text-[#0B132B] border border-[#E9ECEF]"}`}>{m.body}</div>
              <div className={`text-[10px] text-[#6C757D] mt-0.5 ${mine ? "text-right" : ""}`}>{m.author_name} · {m.author_role === "admin" ? "super-admin" : m.author_role} · {new Date(m.created_at).toLocaleString()}</div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="mt-3 flex gap-2">
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={2} placeholder="Type a message…"
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
          className="flex-1 rounded-sm border border-[#0B132B]/15 p-2 text-sm focus:outline-none focus:border-[#D4AF37]" data-testid="onboarding-message-input" />
        <Button onClick={send} disabled={sending || !body.trim()} className="h-auto rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white" data-testid="onboarding-message-send">
          <Send size={14} />
        </Button>
      </div>
    </Card>
  );
}
